// Conversion pieces shared by the public sales pages (/pricing, /start-checkout):
// the walkthrough-video slot and the phone-only sticky "start" bar.
import { ArrowRight, Play } from "lucide-react";
import { useEffect, useState } from "react";

const STREAM_HOST = "https://customer-hguhpmzklz9nabqu.cloudflarestream.com";

/** 16:9 walkthrough video. Accepts a Cloudflare Stream id or an https .mp4
 *  link; with neither it renders a branded "coming soon" frame so the layout
 *  is final before the video exists. */
export function SalesVideo({ src, title, length }: { src: string; title: string; length?: string }) {
  const streamId = /^[a-f0-9]{32}$/i.test(src) ? src : null;
  const mp4 = !streamId && /^https:\/\/\S+$/i.test(src) ? src : null;
  return (
    <figure className="sw-video">
      {streamId ? (
        <iframe
          src={`${STREAM_HOST}/${streamId}/iframe?preload=metadata&poster=${encodeURIComponent(`${STREAM_HOST}/${streamId}/thumbnails/thumbnail.jpg?time=1s`)}`}
          title={title} loading="lazy" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen />
      ) : mp4 ? (
        <video src={mp4} controls playsInline preload="metadata" aria-label={title} />
      ) : (
        <div className="sw-video-soon" role="img" aria-label={`${title} — video coming soon`}>
          <span className="sw-video-play" aria-hidden="true"><Play size={30} fill="currentColor" /></span>
          <strong>{title}</strong>
          <span>Video coming soon{length ? ` · ${length}` : ""}</span>
        </div>
      )}
    </figure>
  );
}

/** Phone-only bar pinned to the bottom of the screen that jumps to the order
 *  form. It hides itself while the form is on screen, so it never covers the
 *  fields or the checkout button. */
export function StickyStart({ targetId, label, sub }: { targetId: string; label: string; sub?: string }) {
  const [formVisible, setFormVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const el = document.getElementById(targetId);
    const onScroll = () => setScrolled(window.scrollY > 240);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    let io: IntersectionObserver | null = null;
    if (el && "IntersectionObserver" in window) {
      io = new IntersectionObserver(([entry]) => setFormVisible(entry.isIntersecting), { threshold: 0.15 });
      io.observe(el);
    } else setFormVisible(false);
    return () => { window.removeEventListener("scroll", onScroll); io?.disconnect(); };
  }, [targetId]);
  const show = scrolled && !formVisible;
  function go() {
    const el = document.getElementById(targetId);
    if (!el) return;
    el.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
    window.setTimeout(() => (el.querySelector("input:not([tabindex='-1'])") as HTMLInputElement | null)?.focus({ preventScroll: true }), 450);
  }
  return (
    <div className={`sw-sticky${show ? " is-on" : ""}`} aria-hidden={!show}>
      <div className="sw-sticky-copy">
        <strong>{label}</strong>
        {sub && <span>{sub}</span>}
      </div>
      <button type="button" className="sw-btn sw-btn-primary" onClick={go} tabIndex={show ? 0 : -1}>
        Start now <ArrowRight size={16} aria-hidden="true" />
      </button>
    </div>
  );
}
